import React from "react";
import RevenuePath from "../revenue-path/RevenuePath";

const PATH_STAGES = [
  { label: "Lead", state: "healthy" },
  { label: "Form / DM", state: "healthy" },
  { label: "CRM", state: "friction" },
  { label: "Booking", state: "leak" },
  { label: "Payment", state: "broken" },
  { label: "Follow-Up", state: "recovered" },
  { label: "Reporting", state: "recovered" },
];

const RECEIVE = [
  "Where leads are entering",
  "Where they are getting stuck",
  "What should be automated",
  "What should be simplified",
  "What should be fixed first",
  "Which build path makes the most sense",
];

/**
 * "What you receive" — process proof for an early-stage studio. Reframes the
 * lead-to-cash map into a deliverable: a revenue-path breakdown across the
 * full Lead → Reporting flow. Dark "operating map" treatment for contrast and
 * the second signature RevenuePath moment on the homepage.
 */
export default function WhatYouReceive() {
  return (
    <section
      data-testid="what-you-receive-section"
      className="px-5 sm:px-6 md:px-10 py-4"
    >
      <div className="max-w-7xl mx-auto">
        <div className="relative rounded-[24px] sm:rounded-[28px] bg-[#151514] grain overflow-hidden px-6 sm:px-10 md:px-14 py-16 sm:py-20 md:py-24">
          <div
            aria-hidden
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(640px 360px at 12% 14%, rgba(194,163,107,0.20), transparent 60%), radial-gradient(640px 360px at 90% 90%, rgba(111,115,94,0.16), transparent 60%)",
            }}
          />

          <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-10 items-end">
            <div className="lg:col-span-7 reveal">
              <p className="eyebrow !text-clay">Process Proof</p>
              <h2
                data-kinetic
                className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-[-0.02em] font-semibold text-[#F4F0E8]"
              >
                What you{" "}
                <span className="italic font-normal text-[#BEB8AF]">receive.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 reveal">
              <p className="text-base md:text-lg text-[#BEB8AF] leading-relaxed">
                A clear revenue-path breakdown of your full path from first
                contact to booked, paid, and followed up.
              </p>
            </div>
          </div>

          {/* The signature path — dark operating map */}
          <div className="relative mt-10 sm:mt-12 reveal">
            <div className="flex items-center justify-between mb-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8E887F]">
                Your revenue path
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
                Leak detected → recovered
              </p>
            </div>
            <div className="border-y border-[#33302B] py-8 sm:py-10 overflow-x-auto">
              <div className="min-w-[620px]">
                <RevenuePath stages={PATH_STAGES} theme="dark" />
              </div>
            </div>
          </div>

          {/* What the breakdown shows */}
          <div className="relative mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {RECEIVE.map((r, idx) => (
              <div
                key={r}
                data-testid={`receive-item-${idx}`}
                className="reveal rounded-2xl border border-[#33302B] bg-[#1A1A18] p-5 sm:p-6"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <p className="mt-3 text-[14.5px] leading-snug text-[#F4F0E8]">
                  {r}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
